import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';

import sortBy from 'lodash/sortBy';
import min from 'lodash/min';
import max from 'lodash/max';
import sum from 'lodash/sum';
import mean from 'lodash/mean';
import {
  asapScheduler,
  map,
  Observable,
  observeOn,
  of,
  Subscription,
  tap,
} from 'rxjs';
import {
  SearchResultsService,
  StatisticsDataItemLike,
} from '../../search-results.service';
import { ScreenService } from 'app/shared/services/screen.service';

interface DescriptiveStatistics {
  min?: number;
  max?: number;
  sum?: number;
  average?: number;
  median?: number;
  standardDeviation?: number;
}

const descritiveStatisticsLabelsMap = new Map<
  keyof DescriptiveStatistics,
  string
>([
  ['min', 'Mínimo'],
  ['max', 'Máximo'],
  ['sum', 'Soma'],
  ['average', 'Média'],
  ['median', 'Mediana'],
  ['standardDeviation', 'Desvio Padrão'],
]);

@Component({
  selector: 'app-results-statistics-view',
  templateUrl: 'results-statistics-view.component.html',
  styleUrls: ['results-statistics-view.component.scss'],
})
export class ResultsStatisticsViewComponent implements OnChanges, OnDestroy {
  totalSizeObservable = this.searchResultsService.observeSearchTotalCount();
  resultList: __esri.Graphic[];
  fieldsObservable = this.searchResultsService.onEstatisticsFieldsLoaded;

  private _currentPageIndex: number;
  @Input()
  pageIndex = 0;
  @Output()
  pageIndexChange = new EventEmitter<number>(true);

  @Input()
  fieldSelected: __esri.Field;
  @Output()
  fieldSelectedChange = new EventEmitter<__esri.Field>();

  private _currentFieldSelected: __esri.Field;

  shouldShowAllDescritiveStatistics: boolean;

  descriptiveStatisticsDisplayedColumns: string[] = ['label', 'value'];
  descriptiveStatisticsList: Array<{
    label: string;
    value: Observable<number>;
  }> = [];

  displayedColumns: string[] = ['alias', 'itemsCount', 'relPercent'];
  itemList: StatisticsDataItemLike[] = [];
  _pagedData: any[];
  pageSize = 7;

  isMobileModeObservable = this.screenService.observeIsMobile();

  private resultListSubscription: Subscription;

  constructor(
    private searchResultsService: SearchResultsService,
    private screenService: ScreenService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.fieldSelected) {
      if (this.fieldSelected !== this._currentFieldSelected) {
        this._currentFieldSelected = this.fieldSelected;
        this.handleResultList(changes.pageIndex?.currentValue ?? 0);
      }
    } else if (changes.pageIndex) {
      this.onPageIndexChange(changes.pageIndex?.currentValue ?? 0);
    }
  }

  ngOnDestroy(): void {
    this.resultListSubscription?.unsubscribe();
  }

  onFieldSelected(item: __esri.Field) {
    this.handleResultList();

    if (this._currentFieldSelected !== item) {
      this._currentFieldSelected = item;
      this.fieldSelectedChange.emit(item);
    }
  }

  onPageIndexChange(pageIndex: number) {
    const lastPageIndex =
      Math.ceil((this.itemList?.length ?? 0) / this.pageSize) - 1 || 0;
    const newPageIndex = Math.min(pageIndex, lastPageIndex);

    if (this._currentPageIndex === newPageIndex) {
      return;
    }
    this._currentPageIndex = newPageIndex;

    const start = newPageIndex * this.pageSize;

    this._pagedData = this.itemList?.slice(start, start + this.pageSize) ?? [];

    if (this.pageIndex !== newPageIndex) {
      this.pageIndex = newPageIndex;
      this.pageIndexChange.emit(this.pageIndex);
    }
  }

  private handleResultList(pageIndex = 0) {
    pageIndex = Math.max(pageIndex, 0);

    this.resultListSubscription?.unsubscribe();

    this._currentPageIndex = null;
    if (!this.fieldSelected) {
      this.descriptiveStatisticsList = [];
      this.shouldShowAllDescritiveStatistics = false;
      this.itemList = null;
      this.onPageIndexChange(0);
      return;
    }

    this.resultListSubscription = this.searchResultsService
      .observeSearchFeaturesAttr(this.fieldSelected.name)
      .pipe(
        map((results) => {
          const domain = this.fieldSelected?.domain as __esri.CodedValueDomain;

          const resultList = results ?? [];

          let list = sortBy(resultList, 'value');

          const descriptiveStatistics: DescriptiveStatistics = {};

          const descriptiveStatisticsKeys: Array<keyof DescriptiveStatistics> =
            [];

          this.shouldShowAllDescritiveStatistics =
            !domain &&
            ['double', 'integer', 'long', 'small-integer'].includes(
              this.fieldSelected.type
            );
          if (this.shouldShowAllDescritiveStatistics) {
            const valueList: number[] = resultList.map((x) => x.value);

            descriptiveStatistics.min = min(valueList);
            descriptiveStatistics.max = max(valueList);

            const statisticsAux = this.calcStatistics(valueList);
            Object.assign(descriptiveStatistics, statisticsAux);

            descriptiveStatisticsKeys.push(
              'min',
              'max',
              'sum',
              'average',
              'median',
              'standardDeviation'
            );
          }

          this.descriptiveStatisticsList = [
            {
              label: 'Contagem',
              value: this.totalSizeObservable,
            },
          ].concat(
            descriptiveStatisticsKeys
              .map((name) => {
                if (!(name in descriptiveStatistics)) {
                  return null;
                }

                const value = descriptiveStatistics[name];
                const label = descritiveStatisticsLabelsMap.get(name as any);
                return { label, value: of(value) };
              })
              .filter((x) => !!x)
          );

          return list;
        }),
        observeOn(asapScheduler),
        tap((list) => {
          this.itemList = list;
          this.onPageIndexChange(pageIndex);
        })
      )
      .subscribe();
  }

  private calcStatistics(list: number[]) {
    const totalSum = sum(list);
    const average = mean(list);
    const median = this.calcMedian(list);

    const standardDeviation = this.calcStandardDeviation(list, average);

    return { sum: totalSum, average, median, standardDeviation };
  }

  private calcStandardDeviation(list: number[], average?: number) {
    if (typeof average === 'number') {
      average = mean(list);
    }

    list = list.map((x) => (x - average) ** 2);

    const totalSum = sum(list);
    const variance = totalSum / list.length;
    return Math.sqrt(variance);
  }

  private calcMedian(list: number[]): number {
    if (!list.length) {
      return 0;
    }

    const firstIndex = Math.round((list.length - 1) / 2);
    const a = list[firstIndex];
    if (list.length % 2 === 0) {
      const b = list[firstIndex + 1];
      return (a + b) / 2;
    } else {
      return a;
    }
  }
}
